/**
 * DAY-DETAILS-1019 (Claude Design return 2026-10-19, Ask 2, frames 2a to 2d, strings 1 to 11) — what a day's
 * stored details look like in the 2.0 day panel.
 *
 * WHAT IS STORED. The engine writes, per day, `warmup`, `main_set`, `cooldown`, `pace_target`, `coach_note` (the
 * session's why), `recovery_nutrition` and, on a clinical adjustment, `clinical_note`
 * (`api/lib/planning/weekMaterializer.js` `sessionToPlanDay`). The adapter (`hooks/useWeeklyPlan.ts`) carries the
 * ones the panel shows on the `Day` as `expanded` (`readDayExpanded` below is the function that reads them), and
 * `DayDetails variant="v2"` draws them. Nothing here fetches, invents or writes anything: a field that is empty
 * after trimming draws no section, and a day with every field empty keeps "No further details for this day."
 *
 * TWO RULES THE FRAMES RATIFIED.
 *   - The pace is shown ONCE, in the panel head. `main_set` embeds it ("20 min continuous tempo at 4:40/km."), so
 *     the main set step drops that one clause, and only when the head states the same pace. A pace the head does
 *     not state (a range, two paces, the other unit: `dayPace` prints nothing for those) stays in the step as
 *     stored, because dropping it would drop it everywhere.
 *   - The text is words. `dayBuilder.js` writes words for new plans; plans stored before that still carry en
 *     dashes, "~" and a multiplication sign, so every field runs through `plainDayText` on its way to the screen
 *     (display only, the stored text is never written back).
 *
 * LEAF MODULE. It imports types, `dayPace` (types only) and `registerPlanText` (no imports), and nothing from
 * `lib/weekMeasure`, so the adapter can import `readDayExpanded` from here without a cycle.
 */
import type { Day } from '../../fixtures/plan';
import type { DistanceUnit } from '../../lib/actualForPlanDay';
import { dayPace } from '../train/dayPace';
import { registerPlanText } from './dayDetailsText';

/** The stored fields the panel shows (`plan_days.expanded_details`), each trimmed and non-empty. */
export type DayExpanded = Partial<
  Record<'warmup' | 'main_set' | 'cooldown' | 'coach_note' | 'recovery_nutrition' | 'clinical_note', string>
>;

const EXPANDED_KEYS = ['warmup', 'main_set', 'cooldown', 'coach_note', 'recovery_nutrition', 'clinical_note'] as const;

/**
 * The adapter's side of the seam: the panel's fields out of a plan day's `expanded_details`. Only strings count,
 * trimmed, and empty ones are dropped. Null when the day stores none of them (the adapter then sets no key).
 * `convert` is the adapter's own text-in-the-athlete's-unit function (`planTextInUnit` bound to the display
 * unit), the one it already runs over `details`, so a warm-up or a main set states its distances in the unit the
 * athlete reads in, like the rest of the panel (B309).
 */
export function readDayExpanded(
  raw: unknown,
  convert?: (text: string) => string | null | undefined,
): DayExpanded | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: DayExpanded = {};
  for (const key of EXPANDED_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value !== 'string') continue;
    const text = (convert ? (convert(value) ?? '') : value).trim();
    if (text) out[key] = text;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * A `Day` as the panel reads it: the day, plus what the adapter adds (`DayWithType` in `useWeeklyPlan.ts`) and
 * the stored details. All the additions are optional, so a plain `Day` (a fixture, a hand-built day) is one.
 */
export type PanelDay = Day & {
  workoutType?: string;
  paceTarget?: string | null;
  paceUnit?: DistanceUnit | null;
  expanded?: DayExpanded | null;
};

/** A range's dash after a number with a short unit suffix ("3k–5k", "20–30g"), before the shared register pass. */
const SUFFIXED_RANGE = /(\d+[a-zA-Z]{0,3})\s*[–—]\s*(?=\d)/g;

/**
 * Stored plan text in words (Ask 2, flag a). The register pass first (`registerPlanText`: ranges read "to",
 * a dash between clauses is a full stop), then what it leaves: "~20" is "about 20", "20g" is "20 g", a rep count
 * "4 × 400" is "4 x 400" (the one rep form the plan readers parse), and the engine's old "easy + 4 strides" and
 * "protein + carbs" phrases read as the frames show them. Text with none of those comes out unchanged.
 */
export function plainDayText(text: string | null | undefined): string {
  if (!text) return '';
  const ranged = text.replace(SUFFIXED_RANGE, '$1 to ');
  return registerPlanText(ranged)
    .replace(/~\s*(?=\d)/g, 'about ')
    .replace(/(\d)\s*×\s*(?=\d)/g, '$1 x ')
    .replace(/(\d)\s?g\b/g, '$1 g')
    .replace(/(\d g)\s+protein\s*\+\s*carbs/gi, '$1 of protein with carbs')
    .replace(/\s\+\s(?=\d|strides\b)/g, ', then ')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/** One pace clause as the engine writes it into prose: " at 4:40/km", " at 8:34 per mile". */
const PACE_CLAUSE = /\s+(?:at|@)\s+(\d{1,2}:\d{2})\s*(?:\/|per\s+)\s*(mi(?:les?)?|km|kilomet(?:er|re)s?)\b/gi;

/**
 * The step's text without its pace clause, when that clause states the pace the head states. `headPace` is
 * `dayPace`'s "5:40 per km" or "8:30 per mile"; null (the head states none) leaves the text whole.
 */
function withoutHeadPace(text: string, headPace: string | null): string {
  if (!headPace) return text;
  const [value, word] = headPace.split(' per ');
  const unit = word === 'mile' ? 'mi' : 'km';
  return text
    .replace(PACE_CLAUSE, (clause: string, v: string, u: string) =>
      v === value && (u.toLowerCase().startsWith('mi') ? 'mi' : 'km') === unit ? '' : clause,
    )
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
}

export type DayStepKey = 'warmup' | 'main' | 'cooldown';

export type DayStep = { key: DayStepKey; label: string; text: string };

export type DaySections = {
  /** A rest day (no session): the head alone, plus Adjusted when the engine inserted the rest for a clinical reason. */
  rest: boolean;
  /** "5:40 per km" once, for the head, or null. */
  pace: string | null;
  /** Adjusted (string 9), the clinical note, or null. */
  adjusted: string | null;
  /** String 1 or 2. */
  whyLabel: 'Why this run' | 'Why this session';
  why: string | null;
  /** The session as a rail (strings 3 to 6): only the steps the day stores. */
  steps: DayStep[];
  /** Recover (string 8), or null. */
  recover: string | null;
  /** String 10 or 11. */
  askLabel: 'Ask about this run' | 'Ask about this day';
  /** Any of adjusted, why, steps, recover: false keeps "No further details for this day." (a run day). */
  any: boolean;
};

/** A day that is not a run: strength, mobility, cross-training. Rest is handled on its own. */
const NOT_A_RUN = /strength|mobility|cross|yoga|swim|bike|cycl|elliptical|gym|pilates/i;

function isRest(day: PanelDay): boolean {
  return day.rest ?? day.mileage === '—';
}

/** Does the day store a clinical note? The rule that lets a Train rest card open (DayCards, Ask 2 flag d). */
export function hasClinicalNote(day: PanelDay): boolean {
  return !!day.expanded?.clinical_note?.trim();
}

/** The panel's sections for one day, every field trimmed, in words, and empty ones dropped. */
export function daySections(day: PanelDay): DaySections {
  const rest = isRest(day);
  const ex = day.expanded ?? null;
  const field = (value: string | null | undefined): string | null => {
    const text = plainDayText(value);
    return text ? text : null;
  };

  const pace = rest ? null : dayPace(day);
  const adjusted = field(ex?.clinical_note);
  const why = rest ? null : field(ex?.coach_note);
  const recover = rest ? null : field(ex?.recovery_nutrition);

  const steps: DayStep[] = [];
  if (!rest) {
    const warmup = field(ex?.warmup);
    // The main set is the stored `main_set`; a day that stores none (a plan written before the engine, a coach's
    // plan) states its session in `details`, which is the same text for an engine day, so nothing prints twice.
    const main = field(withoutHeadPace(plainDayText(ex?.main_set ?? day.details), pace));
    const cooldown = field(ex?.cooldown);
    if (warmup) steps.push({ key: 'warmup', label: 'Warm-up', text: warmup });
    if (main) steps.push({ key: 'main', label: 'Main set', text: main });
    if (cooldown) steps.push({ key: 'cooldown', label: 'Cool-down', text: cooldown });
  }

  const kind = `${day.workoutType ?? ''} ${day.label ?? ''}`;
  const run = !rest && !NOT_A_RUN.test(kind);
  return {
    rest,
    pace,
    adjusted,
    whyLabel: run ? 'Why this run' : 'Why this session',
    why,
    steps,
    recover,
    askLabel: run ? 'Ask about this run' : 'Ask about this day',
    any: !!(adjusted || why || recover || steps.length > 0),
  };
}

/**
 * The Weather row's value in the panel head: the same words the Today hero's row prints
 * ("57° overcast · wind 4"; `routes/today.tsx` `renderLoopHero` builds that string from the same fields).
 */
export function weatherRowText(w: { tempDisplay: string; condition?: string; windMph: number }): string {
  return `${w.tempDisplay}${w.condition ? ` ${w.condition}` : ''} · wind ${w.windMph}`;
}
